// File: src/components/pages/students/StudentProgress.jsx
// Progress tab:
//   Default view  — dropdown + form list (draft pinned top) + New Progress button
//   Form view     — add headings from dropdown then fill only selected sections

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Plus, AlertCircle, CheckCircle2, ChevronLeft,
  Clock, Send, FileText, X
} from 'lucide-react';
import { studentAPI, trackerLevelsAPI, progressTrackerAPI } from '../../../services/api';
import '../../../styles/unified.css';

// ── Field renderer ────────────────────────────────────────────────────────────
const ProgressField = ({ item, type, value, onChange, readOnly = false }) => {
  switch (type) {
    case 'Scale':
      return (
        <div className="pf-scale-wrap">
          <input type="range" min={0} max={5} step={1}
            value={value ?? 0}
            onChange={e => !readOnly && onChange(Number(e.target.value))}
            className="pf-slider" disabled={readOnly} />
          <span className="pf-scale-value">{value ?? 0} / 5</span>
        </div>
      );
    case 'Rating':
      return (
        <div className="pf-scale-wrap">
          <input type="range" min={1} max={5} step={1}
            value={value ?? 1}
            onChange={e => !readOnly && onChange(Number(e.target.value))}
            className="pf-slider" disabled={readOnly} />
          <span className="pf-scale-value">{value ?? 1} / 5</span>
        </div>
      );
    case 'Checkbox':
      return (
        <label className="pf-checkbox-label">
          <input type="checkbox" checked={!!value}
            onChange={e => !readOnly && onChange(e.target.checked)}
            className="pf-checkbox" disabled={readOnly} />
          <span>{value ? 'Completed' : 'Not completed'}</span>
        </label>
      );
    case 'Pass/Fail':
      return (
        <div className="pf-passfail-wrap">
          {['Pass', 'Fail'].map(opt => (
            <label key={opt}
              className={`pf-passfail-btn ${value === opt ? (opt === 'Pass' ? 'pf-pass-active' : 'pf-fail-active') : ''}`}>
              <input type="radio" name={`pf-${item}`} value={opt}
                checked={value === opt}
                onChange={() => !readOnly && onChange(opt)}
                style={{ display: 'none' }} disabled={readOnly} />
              {opt}
            </label>
          ))}
        </div>
      );
    default:
      return (
        <textarea className="pf-textarea" rows={2} placeholder="Add notes…"
          value={value ?? ''} disabled={readOnly}
          onChange={e => !readOnly && onChange(e.target.value)} />
      );
  }
};

const defaultValue = (type) => {
  switch (type) {
    case 'Scale':     return 0;
    case 'Rating':    return 1;
    case 'Checkbox':  return false;
    case 'Pass/Fail': return '';
    default:          return '';
  }
};

const formatDate = (d) => !d ? '—' : new Date(d).toLocaleDateString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
});

const entriestoFormValues = (entries) => {
  const fv = {};
  (entries || []).forEach(entry => {
    fv[entry.templateId] = {};
    (entry.items || []).forEach((itm, idx) => {
      fv[entry.templateId][idx] = itm.value;
    });
  });
  return fv;
};

// ─────────────────────────────────────────────────────────────────────────────
const StudentProgress = () => {
  const { getAccessTokenSilently } = useAuth0();

  // ── Dropdown / list state ──────────────────────────────────────────────────
  const [students,      setStudents]      = useState([]);
  const [isLoading,     setIsLoading]     = useState(false);
  const [loadError,     setLoadError]     = useState(null);
  const [selectedId,    setSelectedId]    = useState('');
  const [entryList,     setEntryList]     = useState([]);
  const [listLoading,   setListLoading]   = useState(false);
  const [expandedIds,   setExpandedIds]   = useState(new Set());

  // ── Form state ─────────────────────────────────────────────────────────────
  const [showForm,        setShowForm]        = useState(false);
  const [formLoading,     setFormLoading]     = useState(false);
  const [formError,       setFormError]       = useState(null);
  const [levelConfig,     setLevelConfig]     = useState(null);   // full config
  const [trackerLevelId,  setTrackerLevelId]  = useState(null);
  const [progressLevel,   setProgressLevel]   = useState(null);
  const [formValues,      setFormValues]      = useState({});
  const [editingId,       setEditingId]       = useState(null);
  const [isBusy,          setIsBusy]          = useState(false);
  const [formSuccess,     setFormSuccess]     = useState(null);

  // ── Heading-picker state ───────────────────────────────────────────────────
  // activeTemplates  — headings the instructor has added to this entry
  // availableTemplates — headings not yet added (feeds the dropdown)
  // selectedHeading  — templateId of the dropdown's current selection
  const [activeTemplates,    setActiveTemplates]    = useState([]);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedHeading,    setSelectedHeading]    = useState('');

  // ── Load active students ───────────────────────────────────────────────────
  const loadActiveStudents = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await studentAPI.getStudentsByStatus('active', getAccessTokenSilently);
      if (res.success) setStudents(res.data || []);
      else setLoadError(res.message || 'Failed to load students');
    } catch {
      setLoadError('Failed to load students. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => { loadActiveStudents(); }, [loadActiveStudents]);

  // ── Load progress entries when student changes ────────────────────────────
  const loadStudentEntries = useCallback(async (studentId) => {
    if (!studentId) { setEntryList([]); return; }
    setListLoading(true);
    try {
      const res = await progressTrackerAPI.getProgressByStudent(studentId, getAccessTokenSilently);
      const data = res.success ? res.data || [] : [];
      setEntryList(data);
      const draft = data.find(e => e.status === 'draft');
      if (draft) setExpandedIds(new Set([draft.progressId]));
      else setExpandedIds(new Set());
    } catch {
      setEntryList([]);
    } finally {
      setListLoading(false);
    }
  }, [getAccessTokenSilently]);

  const handleStudentChange = (e) => {
    const id = e.target.value;
    setSelectedId(id);
    setShowForm(false);
    loadStudentEntries(id);
  };

  const selectedStudent = students.find(s => s.studentId === selectedId) || null;
  const draftEntry      = entryList.find(e => e.status === 'draft') || null;

  // ── Open new form ──────────────────────────────────────────────────────────
  const handleNewProgress = async () => {
    if (!selectedId) return;
    if (draftEntry) { openDraftForEditing(draftEntry); return; }

    setFormLoading(true);
    setFormError(null);
    setEditingId(null);
    setFormSuccess(null);
    setActiveTemplates([]);
    setSelectedHeading('');

    try {
      const level = selectedStudent?.progressLevel ?? 1;
      setProgressLevel(level);

      const res = await trackerLevelsAPI.getAll(getAccessTokenSilently);
      if (!res.success || !res.data || res.data.length === 0) {
        setFormError('No published tracker configuration found. Publish a configuration first.');
        setShowForm(true);
        return;
      }

      const record  = res.data[0];
      setTrackerLevelId(record.trackerLevelId);

      const matched = (record.levels || []).find(lv => Number(lv.level) === Number(level));
      if (!matched) {
        setFormError(`No configuration for Level ${level}. Configure this level in the Tracker.`);
        setShowForm(true);
        return;
      }

      setLevelConfig(matched);
      setAvailableTemplates(matched.templates || []);
      setFormValues({});
      setShowForm(true);
    } catch (err) {
      setFormError(err.message || 'Failed to load tracker configuration.');
      setShowForm(true);
    } finally {
      setFormLoading(false);
    }
  };

  // ── Re-open existing draft for editing ────────────────────────────────────
  const openDraftForEditing = async (draft) => {
    setFormLoading(true);
    setFormError(null);
    setFormSuccess(null);
    setEditingId(draft.progressId);
    setSelectedHeading('');

    try {
      const level = draft.progressLevel;
      setProgressLevel(level);
      setTrackerLevelId(draft.trackerLevelId);

      // Fetch the FULL level config from the tracker API so that templates
      // not yet in this draft can populate the availableTemplates dropdown.
      const res = await trackerLevelsAPI.getAll(getAccessTokenSilently);
      if (!res.success || !res.data || res.data.length === 0) {
        setFormError('No published tracker configuration found.');
        setShowForm(true);
        return;
      }

      const record  = res.data[0];
      const matched = (record.levels || []).find(lv => Number(lv.level) === Number(level));
      if (!matched) {
        setFormError(`No configuration found for Level ${level}.`);
        setShowForm(true);
        return;
      }

      setLevelConfig(matched); // full config — required by handleRemoveHeading for ordering

      // Split full config into active (already in draft) vs available (not yet added)
      const draftTemplateIds = new Set(
        (draft.entries || []).map(e => e.templateId)
      );

      // activeTemplates: config templates that ARE in the draft (preserves item metadata)
      const activeFromDraft = (matched.templates || [])
        .filter(t => draftTemplateIds.has(t.templateId));

      // availableTemplates: config templates the instructor has NOT added yet → feeds dropdown
      const available = (matched.templates || [])
        .filter(t => !draftTemplateIds.has(t.templateId));

      setActiveTemplates(activeFromDraft);
      setAvailableTemplates(available);

      setFormValues(entriestoFormValues(draft.entries));
      setShowForm(true);
    } finally {
      setFormLoading(false);
    }
  };

  // ── Add selected heading to the active list ───────────────────────────────
  const handleAddHeading = () => {
    if (!selectedHeading) return;
    const tpl = availableTemplates.find(t => t.templateId === selectedHeading);
    if (!tpl) return;

    // Initialise default values for this template's items
    setFormValues(prev => ({
      ...prev,
      [tpl.templateId]: Object.fromEntries(
        (tpl.items || []).map((itm, idx) => [idx, defaultValue(itm.type)])
      )
    }));

    setActiveTemplates(prev => [...prev, tpl]);
    setAvailableTemplates(prev => prev.filter(t => t.templateId !== selectedHeading));
    setSelectedHeading('');
  };

  // ── Remove a heading from the active list ─────────────────────────────────
  const handleRemoveHeading = (templateId) => {
    const tpl = activeTemplates.find(t => t.templateId === templateId);
    if (!tpl) return;
    setActiveTemplates(prev => prev.filter(t => t.templateId !== templateId));
    setAvailableTemplates(prev => {
      // Re-insert in original levelConfig order
      const allTemplates = levelConfig?.templates || [];
      const remaining = [...prev, tpl];
      return allTemplates.filter(t => remaining.some(r => r.templateId === t.templateId));
    });
    // Remove values for this template
    setFormValues(prev => {
      const next = { ...prev };
      delete next[templateId];
      return next;
    });
  };

  // ── Field change ──────────────────────────────────────────────────────────
  const handleValueChange = (templateId, itemIdx, value) => {
    setFormValues(prev => ({
      ...prev,
      [templateId]: { ...prev[templateId], [itemIdx]: value }
    }));
  };

  // ── Build entries array from active templates only ────────────────────────
  const buildEntries = () =>
    activeTemplates.map(tpl => ({
      templateId: tpl.templateId,
      heading:    tpl.heading,
      items:      (tpl.items || []).map((itm, idx) => ({
        item:  itm.item,
        type:  itm.type,
        value: formValues[tpl.templateId]?.[idx] ?? defaultValue(itm.type)
      }))
    }));

  // ── Save as draft ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (activeTemplates.length === 0) {
      setFormError('Please add at least one heading before saving.');
      return;
    }
    setIsBusy(true);
    setFormError(null);
    setFormSuccess(null);
    try {
      const entries = buildEntries();
      let res;
      if (editingId) {
        res = await progressTrackerAPI.updateProgress(
          editingId, { entries, status: 'draft' }, getAccessTokenSilently
        );
      } else {
        res = await progressTrackerAPI.createProgress(
          { studentId: selectedId, progressLevel, trackerLevelId, entries, status: 'draft' },
          getAccessTokenSilently
        );
        if (res.success) setEditingId(res.data.progressId);
      }
      if (res.success) {
        setFormSuccess('draft');
        await loadStudentEntries(selectedId);
        setTimeout(() => setFormSuccess(null), 3000);
      } else {
        setFormError(res.message || 'Failed to save draft.');
      }
    } catch (err) {
      setFormError(err.message || 'Failed to save draft.');
    } finally {
      setIsBusy(false);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (activeTemplates.length === 0) {
      setFormError('Please add at least one heading before submitting.');
      return;
    }
    setIsBusy(true);
    setFormError(null);
    setFormSuccess(null);
    try {
      const entries = buildEntries();
      let res;
      if (editingId) {
        res = await progressTrackerAPI.updateProgress(
          editingId, { entries, status: 'submitted' }, getAccessTokenSilently
        );
      } else {
        res = await progressTrackerAPI.createProgress(
          { studentId: selectedId, progressLevel, trackerLevelId, entries, status: 'submitted' },
          getAccessTokenSilently
        );
      }
      if (res.success) {
        setFormSuccess('submitted');
        await loadStudentEntries(selectedId);
        setTimeout(() => {
          setFormSuccess(null);
          setShowForm(false);
          setFormError(null);
          setLevelConfig(null);
          setEditingId(null);
          setActiveTemplates([]);
          setAvailableTemplates([]);
        }, 1500);
      } else {
        setFormError(res.message || 'Failed to submit progress.');
      }
    } catch (err) {
      setFormError(err.message || 'Failed to submit progress.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleBack = () => {
    setShowForm(false);
    setFormError(null);
    setLevelConfig(null);
    setEditingId(null);
    setActiveTemplates([]);
    setAvailableTemplates([]);
    setSelectedHeading('');
  };

  // ── Form view ─────────────────────────────────────────────────────────────
  if (showForm) {
    return (
      <div className="pf-wrap">

        {/* Header */}
        <div className="pf-header">
          <button className="btn-secondary pf-back" onClick={handleBack}>
            <ChevronLeft size={16} /> Back
          </button>
          <div className="pf-header-info">
            <span className="pf-header-student">{selectedStudent?.name}</span>
            <span className="pf-header-level">Level {progressLevel}</span>
            {editingId && <span className="pf-draft-badge">Editing Draft</span>}
          </div>
        </div>

        {formLoading && (
          <div className="loading-state"><div className="spinner" /><p>Loading…</p></div>
        )}

        {formError && (
          <div className="error-banner" style={{ marginBottom: '1rem' }}>
            <AlertCircle size={16} /><span>{formError}</span>
          </div>
        )}
        {formSuccess === 'draft' && (
          <div className="success-banner" style={{ marginBottom: '1rem' }}>
            <CheckCircle2 size={16} /><span>Draft saved successfully.</span>
          </div>
        )}
        {formSuccess === 'submitted' && (
          <div className="success-banner" style={{ marginBottom: '1rem' }}>
            <CheckCircle2 size={16} /><span>Progress submitted. Redirecting…</span>
          </div>
        )}

        {!formLoading && levelConfig && (
          <>
            {/* ── Heading picker ───────────────────────────────────────────── */}
            {availableTemplates.length > 0 && (
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  padding: '0.85rem 1rem',
                  background: '#f8f9ff',
                  border: '1.5px solid #e0e7ff',
                  borderRadius: '0.6rem'
                }}>
                  <select
                    value={selectedHeading}
                    onChange={e => setSelectedHeading(e.target.value)}
                    style={{
                      flex: 1, padding: '0.5rem 0.75rem',
                      border: '1.5px solid #d1d5db', borderRadius: '0.5rem',
                      fontSize: '0.9rem', fontFamily: 'inherit',
                      background: '#fff', color: '#111827', outline: 'none'
                    }}
                  >
                    <option value="">Select a heading to add…</option>
                    {availableTemplates.map(tpl => (
                      <option key={tpl.templateId} value={tpl.templateId}>
                        {tpl.heading}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAddHeading}
                    disabled={!selectedHeading}
                    className="btn-new"
                    title={selectedHeading ? 'Click to add to your preferred list' : undefined}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.35rem',
                      opacity: selectedHeading ? 1 : 0.45,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Plus size={16} /> Add
                  </button>
                </div>

                {/* Guidance shown the moment a heading is picked — the hover
                    tooltip on the button alone is easy to miss, so this makes
                    the next step visible without needing to hover anything. */}
                {selectedHeading && (
                  <p style={{
                    margin: '0.5rem 0 0', padding: '0 0.25rem',
                    fontSize: '0.82rem', color: '#4f46e5',
                    display: 'flex', alignItems: 'center', gap: '0.35rem'
                  }}>
                    <Plus size={13} />
                    Click <strong>+ Add</strong> to add it to your preferred list.
                  </p>
                )}
              </div>
            )}

            {/* ── Empty state when nothing added yet ───────────────────────── */}
            {activeTemplates.length === 0 && (
              <div style={{
                textAlign: 'center', padding: '2.5rem 1rem',
                color: '#9ca3af', fontSize: '0.9rem',
                border: '2px dashed #e5e7eb', borderRadius: '0.75rem',
                marginBottom: '1.25rem'
              }}>
                <FileText size={32} style={{ marginBottom: '0.5rem', color: '#d1d5db' }} />
                <p style={{ margin: 0 }}>
                  Select a heading from the dropdown above and click <strong>Add</strong> to begin.
                </p>
              </div>
            )}

            {/* ── Active template sections ──────────────────────────────────── */}
            {activeTemplates.map(tpl => (
              <div key={tpl.templateId} className="pf-template-section" style={{ position: 'relative' }}>

                {/* Section header + remove button */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <h4 className="pf-template-heading" style={{ margin: 0 }}>{tpl.heading}</h4>
                  <button
                    type="button"
                    onClick={() => handleRemoveHeading(tpl.templateId)}
                    title="Remove this section"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#9ca3af', padding: '0.25rem', display: 'flex',
                      alignItems: 'center', borderRadius: '0.25rem'
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                    onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="pf-items-list">
                  {(tpl.items || []).map((itm, idx) => (
                    <div key={idx} className="pf-item-row">
                      <label className="pf-item-label">{itm.item}</label>
                      <div className="pf-item-input">
                        <ProgressField
                          item={itm.item}
                          type={itm.type}
                          value={formValues[tpl.templateId]?.[idx]}
                          onChange={val => handleValueChange(tpl.templateId, idx, val)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* ── Footer actions ────────────────────────────────────────────── */}
            <div className="pf-footer">
              <button className="btn-secondary" onClick={handleBack} disabled={isBusy}>
                Cancel
              </button>
              <button className="btn-secondary pf-save-btn" onClick={handleSave} disabled={isBusy}>
                {isBusy ? '…' : <><FileText size={15} style={{ marginRight: 5 }} />Save</>}
              </button>
              <button className="btn-new" onClick={handleSubmit} disabled={isBusy}
                style={{ opacity: isBusy ? 0.55 : 1 }}>
                {isBusy ? 'Submitting…' : <><Send size={15} style={{ marginRight: 5 }} />Submit</>}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ── Helpers for list view ─────────────────────────────────────────────────
  const toggleCard = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

// ── Scale colour — hue 0 (red) → 120 (green) as value rises 0→5 ─────────────
const scaleColor = (value) => {
  const hue = Math.round((Math.max(0, Math.min(value, 5)) / 5) * 120);
  return `hsl(${hue}, 80%, 42%)`;
};

  const renderValue = (itm) => {
    const { type, value } = itm;
    if (type === 'Scale') {
      const pct = Math.round((value / 5) * 100);
      return (
        <div className="ev-bar-wrap">
          <div className="ev-bar">
            <div className="ev-bar-fill"
              style={{ width: `${pct}%`, backgroundColor: scaleColor(value) }} />
          </div>
          <span className="ev-score" style={{ color: scaleColor(value), fontWeight: 700 }}>
            {value} / 5
          </span>
        </div>
      );
    }
    if (type === 'Rating') {
      const pct = Math.round(((value - 1) / 4) * 100);
      return (
        <div className="ev-bar-wrap">
          <div className="ev-bar"><div className="ev-bar-fill" style={{ width: `${pct}%` }} /></div>
          <span className="ev-score">{value} / 5</span>
        </div>
      );
    }
    if (type === 'Checkbox') {
      return value
        ? <span className="ev-pill ev-pill-yes">✓ Completed</span>
        : <span className="ev-pill ev-pill-no">Pending</span>;
    }
    if (type === 'Pass/Fail') {
      return value === 'Pass'
        ? <span className="ev-pill ev-pill-pass">Pass</span>
        : value === 'Fail'
          ? <span className="ev-pill ev-pill-fail">Fail</span>
          : <span className="ev-muted">—</span>;
    }
    return <span className="ev-note">{value || <span className="ev-muted">No comments</span>}</span>;
  };

  // ── Default view ─────────────────────────────────────────────────────────
  return (
    <div className="sp-wrap">

      <div className="sp-layout">
        <div className="sp-dropdown-wrap">
          <label className="sp-dropdown-label">Select Student</label>
          {loadError && (
            <div className="error-banner" style={{ marginBottom: '0.5rem' }}>
              <AlertCircle size={15} /><span>{loadError}</span>
            </div>
          )}
          <select className="sp-dropdown" value={selectedId}
            onChange={handleStudentChange}
            disabled={isLoading || students.length === 0}>
            <option value="">
              {isLoading ? 'Loading…' : students.length === 0 ? 'No active students' : 'Choose a student…'}
            </option>
            {students.map(s => (
              <option key={s.studentId} value={s.studentId}>{s.name} ({s.phone?.replace(/^\+440/, '0').replace(/^\+447/, '07')})</option>
            ))}
          </select>
        </div>

        <div className="sp-actions">
          <button className="btn-new" onClick={handleNewProgress}
            disabled={!selectedId}
            style={{ opacity: selectedId ? 1 : 0.45 }}>
            <Plus size={18} />
            {draftEntry ? 'Continue Draft' : 'New Progress'}
          </button>
        </div>
      </div>

      {selectedId && (
        <div className="sp-entry-list">
          {listLoading ? (
            <div className="loading-state" style={{ padding: '1.5rem' }}>
              <div className="spinner" /><p>Loading forms…</p>
            </div>
          ) : entryList.length === 0 ? (
            <div className="sp-entry-empty">
              <FileText size={32} style={{ color: '#cbd5e1' }} />
              <p>No progress forms yet for this student.</p>
            </div>
          ) : (
            entryList.map((entry, entryIdx) => {
              const isFirst = entryIdx === 0;
              const isOpen  = isFirst || expandedIds.has(entry.progressId);
              const isDraft = entry.status === 'draft';

              return (
                <div
                  key={entry.progressId}
                  className={`sp-entry-card ${isDraft ? 'sp-entry-draft' : 'sp-entry-submitted'}`}
                >
                  <div
                    className="sp-entry-hdr"
                    onClick={() => !isFirst && toggleCard(entry.progressId)}
                    style={{ cursor: isFirst ? 'default' : 'pointer' }}
                  >
                    <div className="sp-entry-hdr-left">
                      <span className={`sp-entry-badge ${isDraft ? 'badge-draft' : 'badge-submitted'}`}>
                        {isDraft ? <><Clock size={11} /> Draft</> : <><CheckCircle2 size={11} /> Submitted</>}
                      </span>
                      <span className="sp-entry-level">Level {entry.progressLevel}</span>
                    </div>
                    <div className="sp-entry-hdr-right">
                      <span className="sp-entry-date">
                        {isDraft
                          ? `Saved ${formatDate(entry.updatedAt)}`
                          : `Submitted ${formatDate(entry.submittedAt)}`}
                      </span>
                      {isDraft && (
                        <button
                          className="sp-entry-edit btn-secondary"
                          onClick={e => { e.stopPropagation(); openDraftForEditing(entry); }}
                        >
                          Edit
                        </button>
                      )}
                      {!isFirst && (
                        <span className="sp-chevron">{isOpen ? '▼' : '▶'}</span>
                      )}
                    </div>
                  </div>

                  {isOpen && (
                    <div className="sp-entry-body">
                      {(entry.entries || []).map((tpl, ti) => (
                        <div key={tpl.templateId} className={`ev-section${ti > 0 ? ' ev-section-border' : ''}`}>
                          <div className="ev-section-heading">{tpl.heading}</div>
                          <div className="ev-col-table">
                            <div className="ev-col-headers">
                              {(tpl.items || []).map((itm, i) => (
                                <div key={i} className="ev-col-header">{itm.item}</div>
                              ))}
                            </div>
                            <div className="ev-col-values">
                              {(tpl.items || []).map((itm, i) => (
                                <div key={i} className="ev-col-value">
                                  {renderValue(itm)}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default StudentProgress;